/**
 * Estação de trabalho do controlador: a intimação organizada à esquerda
 * (tribunal, partes, datas, links, texto com destaques, resumo por IA) e, à
 * direita, o panorama do processo e a análise — providências com responsável
 * sugerido e mapa de carga da equipe para escolher o dia planejado.
 * "Anterior/Próxima" percorre a fila sem sair da tela.
 */
import { StartFlowModal } from "@/ui/bpm";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { Link, navigate, useSearch, useTitle } from "@/lib/router";
import { COMM_STATUS_LABEL, fmtDate, fmtDateTime, SOURCE_LABEL, STATE_LABEL } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import {
  Badge, Card, DataTable, EntityPicker, ErrorBox, Field, FormGrid, Loading, StageBadge, Tabs, TypeSelect, useAct, type EntityRef, Cnj,
} from "@/ui/kit";
import { Icon } from "@/ui/icons";
import { NewProcessModal } from "./ProcessPages";

// --------------------------------------------------------------------- tipos

interface Parsed {
  tribunal: string | null; organ: string | null; className: string | null; kind: string | null; documentType: string | null; medium: string | null;
  parties: Array<{ name: string; pole: "ativo" | "passivo" | "outro"; isClient?: boolean }>;
  lawyers: Array<{ name: string; oab: string | null; uf: string | null; ours: boolean }>;
  links: { tribunal: string | null; certidao: string | null };
  text: string;
  highlights: Array<{ start: number; end: number; kind: "prazo" | "ato" | "data" | "audiencia" }>;
  deadline: { days: number; unit: "dias" | "horas"; excerpt: string } | null;
  hearing: { date: string; time: string | null; excerpt: string } | null;
}
interface Act { id: string; number: number; title: string; state: string; responsibleUserId: string | null; internalDueAt: string | null; plannedAt?: string | null; fatalDueAt?: string | null; completedAt?: string | null }
interface Panel {
  communication: {
    id: string; cnjNumber: string | null; organName: string | null; kind: string | null; availableAt: string | null; status: string;
    analysisNote: string | null; analyzedBy: string | null; analyzedAt: string | null; processId: string | null;
    obligations: Array<{ id: string; description: string; fatalAt: string | null; activityId: string | null; verification: string }>;
    captures: Array<{ id: string; source: string; externalId: string; capturedAt: string; payload: { _via: string | null; _oab: string | null } }>;
  };
  parsed: Parsed;
  dates: { disponibilizacao: string | null; publicacao: string | null; inicioPrazo: string | null; fatalSugerido: string | null; internoSugerido: string | null };
  process: null | {
    id: string; cnjNumber: string | null; title: string | null; organName: string; officialLink: string | null; responsibleUserId: string; stageId: string | null;
    className: string | null; phase: string; situation: string; instance: string | null; claimValue: string | null; lastReviewedAt: string | null;
    case: { id: string; number: number; title: string; persons: Array<{ role: string; person: { id: string; name: string } }> } | null;
    persons: Array<{ role: string; person: { id: string; name: string } }>;
  };
  panorama: null | {
    openActivities: Act[]; recentDone: Act[];
    openObligations: Array<{ id: string; description: string; fatalAt: string | null }>;
    previous: Array<{ id: string; availableAt: string | null; kind: string | null; status: string; text: string }>;
    movements?: Array<{ id: string; name: string; complement: string | null; occurredAt: string; relevant: boolean; matchedCommunicationId: string | null }>;
  };
  nav: { fila: string; total: number; position: number | null; prevId: string | null; nextId: string | null; filtered?: boolean };
  settings: { internoDiasAntes: number };
  portals: Array<{ tribunal: string; label: string; url: string }>;
  otherPortals: Array<{ tribunal: string; label: string; url: string }>;
  portalReason: string;
  ai: { available: boolean; summary: AiSummary | null; at: string | null; model: string | null };
}
interface AiSummary {
  resumo?: string; ato?: string; urgencia?: string; observacoes?: string | null;
  providencias?: Array<{ o_que: string; prazo_dias: number | null; tipo_prazo: string | null; calc?: { publicacao: string; fatal: string; interno: string; regra: string } }>;
  /** Prazo lido do texto pela regra do sistema (para comparar com a sugestão da IA). */
  prazoSistema?: string | null;
  audiencia?: { data: string; hora: string | null; modalidade: string | null } | null;
}
interface Carga {
  from: string; until: string; days: Array<{ date: string; holiday: boolean }>;
  users: Array<{ id: string; name: string; perDay: Record<string, number>; fatalPerDay: Record<string, number>; window: number; open: number; overdue: number }>;
  suggestions: Array<{ userId: string; reasons: string[] }>;
}
interface Provid {
  description: string; days: string; fatalAt: string; suggestedFatalAt: string; createActivity: boolean;
  title: string; typeId: string | null; responsibleUserId: string | null; plannedAt: string; internalDueAt: string;
}

const POLE: Record<string, string> = { ativo: "Polo ativo", passivo: "Polo passivo", outro: "Outros" };
const HL_CLASS: Record<string, string> = { prazo: "hl-prazo", ato: "hl-ato", data: "hl-data", audiencia: "hl-aud" };

function addBusinessBack(fatal: string, n: number, days: Carga["days"]): string {
  // Anda para trás nos dias úteis conhecidos (a grade já exclui fins de semana; feriados marcados).
  const useful = days.filter((d) => !d.holiday && d.date < fatal).map((d) => d.date);
  return useful[useful.length - n] ?? useful[0] ?? fatal;
}

// ---------------------------------------------------------------- página

/** Filtros da lista de intimações que a tela cheia repassa à API (mesmos nomes da API). */
export const COMM_FILTER_KEYS = ["from", "to", "dateBasis", "source", "oab", "lawyer", "responsible", "unlinked", "late", "q"] as const;

export function CommWorkspace({ id }: { id: string }): JSX.Element {
  const [search] = useSearch();
  const fila = search.get("fila") ?? "nao_analisada";
  // Filtros da lista de origem (advogado, responsável, período…): a fila anterior/próxima segue o mesmo filtro.
  const fq: Record<string, string> = {};
  for (const k of COMM_FILTER_KEYS) { const v = search.get(k); if (v) fq[k] = v; }
  const { data, error, isFetching } = useQuery({
    queryKey: ["comunicacao-painel", id, fila, fq],
    queryFn: () => api.get<Panel>(`/comunicacoes/${id}/painel${qs({ fila, ...fq })}`),
  });
  useTitle(data?.communication.cnjNumber ?? "Intimação");
  const go = (to: string | null) => { if (to) navigate(`/controladoria/intimacoes/${to}${qs({ fila, ...fq })}`); };

  // Atalhos: Alt+← / Alt+→ percorrem a fila.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || !data) return;
      if (e.key === "ArrowRight") go(data.nav.nextId);
      if (e.key === "ArrowLeft") go(data.nav.prevId);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading lines={8} />;
  const { communication: c, parsed: p, nav } = data;
  const pending = c.status === "nao_analisada" || c.status === "conferir";

  return (
    <div className="stack gap-12">
      <div className="ws-top">
        <Link to={`/controladoria${qs({ aba: fila === "nao_analisada" ? "caixa" : fila === "conferir" ? "movimentacoes" : "todas" })}`} className="btn ghost small">← Controladoria</Link>
        <div className="grow">
          <div className="ws-title">
            {c.cnjNumber ? <Cnj value={c.cnjNumber} /> : "Comunicação sem número"}
            <Badge tone={pending ? "warn" : "neutral"}>{COMM_STATUS_LABEL[c.status] ?? c.status}</Badge>
            {isFetching && <span className="muted small">atualizando…</span>}
          </div>
          <div className="muted small">{[p.tribunal, p.organ, p.className].filter(Boolean).join(" · ")}</div>
        </div>
        {nav.position && <span className="muted small">{nav.position} de {nav.total} na fila{nav.filtered ? " filtrada" : ""}</span>}
        {nav.filtered && <Badge tone="info">fila com filtro</Badge>}
        <button className="btn small" disabled={!nav.prevId} onClick={() => go(nav.prevId)} title="Alt + ←">← Anterior</button>
        <button className="btn small" disabled={!nav.nextId} onClick={() => go(nav.nextId)} title="Alt + →">Próxima →</button>
      </div>

      <div className="ws-grid">
        <div className="stack gap-12 ws-col">
          <SummaryCard d={data} />
          {data.ai.available && <AiCard d={data} />}
          <Card title="Texto da intimação">
            <HighlightedText text={p.text} hl={p.highlights} />
            <div className="row small muted" style={{ marginTop: 8, gap: 10 }}>
              <span><mark className="hl-prazo">prazo</mark></span><span><mark className="hl-ato">ato/ordem</mark></span>
              <span><mark className="hl-aud">audiência</mark></span><span><mark className="hl-data">data</mark></span>
            </div>
          </Card>
          <Sources c={c} />
        </div>
        <div className="stack gap-12 ws-col">
          <ProcessCard d={data} />
          {pending ? <Analysis d={data} onDone={() => (nav.nextId ? go(nav.nextId) : navigate("/controladoria"))} /> : <Result d={data} />}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------- coluna esquerda

function SummaryCard({ d }: { d: Panel }): JSX.Element {
  const p = d.parsed;
  const poles = (["ativo", "passivo", "outro"] as const).map((k) => [k, p.parties.filter((x) => x.pole === k)] as const).filter(([, xs]) => xs.length);
  return (
    <Card title="Intimação">
      <div className="kv-grid">
        <div><span>Tribunal</span><b>{p.tribunal ?? "—"}</b></div>
        <div className="span-2"><span>Órgão</span><b>{p.organ ?? "—"}</b></div>
        <div><span>Tipo</span><b>{[p.kind, p.documentType].filter(Boolean).join(" · ") || "—"}</b></div>
        <div className="span-2"><span>Classe</span><b>{p.className ?? "—"}</b></div>
      </div>
      <div className="date-strip">
        <div><span>Disponibilizada</span><b>{fmtDate(d.dates.disponibilizacao)}</b></div>
        <div><span>Publicação</span><b>{fmtDate(d.dates.publicacao)}</b></div>
        <div><span>Início do prazo</span><b>{fmtDate(d.dates.inicioPrazo)}</b></div>
        {d.dates.fatalSugerido && <div className="bad"><span>Fatal sugerido</span><b>{fmtDate(d.dates.fatalSugerido)}</b></div>}
      </div>
      {(p.deadline || p.hearing) && (
        <div className="stack gap-4" style={{ marginTop: 10 }}>
          {p.deadline && <div className="detect"><Icon name="clock" size="sm" /> <b>Prazo detectado: {p.deadline.days} {p.deadline.unit}</b> <span className="muted">— “{p.deadline.excerpt.slice(0, 180)}”</span></div>}
          {p.hearing && <div className="detect"><Icon name="cal" size="sm" /> <b>Audiência: {fmtDate(p.hearing.date)}{p.hearing.time ? ` às ${p.hearing.time}` : ""}</b></div>}
        </div>
      )}
      {poles.length > 0 && (
        <div className="parties">
          {poles.map(([k, xs]) => (
            <div key={k}><div className="fld-label">{POLE[k]}</div>
              {xs.map((x) => <div key={x.name}>{x.name} {x.isClient && <Badge tone="ok">cliente</Badge>}</div>)}
            </div>
          ))}
        </div>
      )}
      {p.lawyers.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="fld-label">Advogados intimados</div>
          <div className="row" style={{ gap: 6 }}>
            {p.lawyers.map((l) => <Badge key={l.name} tone={l.ours ? "info" : "muted"}>{l.name}{l.oab ? ` · OAB ${l.oab}/${l.uf ?? ""}` : ""}{l.ours ? " · nossa" : ""}</Badge>)}
          </div>
        </div>
      )}
      <div className="row" style={{ marginTop: 12, gap: 6 }}>
        {d.portals.map((x) => (
          <a key={x.url} className="btn small primary" href={x.url} target="_blank" rel="noreferrer" title={`Acesso direto ao sistema do tribunal (pede login) — escolhido por: ${d.portalReason}`}>
            <Icon name="ext" size="sm" /> {x.label}
          </a>
        ))}
        {d.otherPortals.map((x) => (
          <a key={x.url} className="btn small ghost" href={x.url} target="_blank" rel="noreferrer" title="Outro sistema do mesmo tribunal">{x.label}</a>
        ))}
        {!d.portals.length && p.tribunal && <PortalQuickAdd tribunal={p.tribunal} />}
        {p.links.tribunal && <a className="btn small" href={p.links.tribunal} target="_blank" rel="noreferrer" title="Página de validação do documento enviada pelo DJEN">Validação do documento</a>}
        {p.links.certidao && <a className="btn small" href={p.links.certidao} target="_blank" rel="noreferrer">Certidão DJEN</a>}
        {d.process?.officialLink && <a className="btn small" href={d.process.officialLink} target="_blank" rel="noreferrer">Processo no tribunal</a>}
      </div>
    </Card>
  );
}

/** Cadastra na hora o acesso direto do tribunal (só administrador). */
function PortalQuickAdd({ tribunal }: { tribunal: string }): JSX.Element | null {
  const { can } = useLookups();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const save = useAct(async () => {
    const cur = await api.get<{ portals: Array<{ tribunal: string; label: string; url: string }> }>("/config/tribunais");
    return api.put("/config/tribunais", { portals: [...cur.portals, { tribunal, label: `${tribunal} — painel do advogado`, url }] });
  }, [["comunicacao-painel"], ["config-tribunais"]], () => setOpen(false), "Acesso cadastrado");
  if (!can("config.gerenciar")) return <span className="muted small">Sem acesso direto cadastrado para o {tribunal}.</span>;
  if (!open) return <button className="btn small ghost" onClick={() => setOpen(true)}>+ acesso direto do {tribunal}</button>;
  return (
    <span className="row nowrap">
      <input className="input" style={{ width: 320 }} placeholder="https://pje.../Painel/painel_usuario/advogado.seam" value={url} onChange={(e) => setUrl(e.target.value)} autoFocus />
      <button className="btn small primary" disabled={!/^https?:\/\//.test(url) || save.isPending} onClick={() => save.mutate()}>Salvar</button>
      <button className="btn small ghost" onClick={() => setOpen(false)}>✕</button>
    </span>
  );
}

function HighlightedText({ text, hl }: { text: string; hl: Parsed["highlights"] }): JSX.Element {
  const parts: JSX.Element[] = [];
  let pos = 0;
  hl.forEach((h, i) => {
    if (h.start > pos) parts.push(<Fragment key={`t${i}`}>{text.slice(pos, h.start)}</Fragment>);
    parts.push(<mark key={`h${i}`} className={HL_CLASS[h.kind]}>{text.slice(h.start, h.end)}</mark>);
    pos = h.end;
  });
  parts.push(<Fragment key="end">{text.slice(pos)}</Fragment>);
  return <div className="comm-text ws-text">{parts}</div>;
}

function AiCard({ d }: { d: Panel }): JSX.Element {
  const [s, setS] = useState<AiSummary | null>(d.ai.summary);
  useEffect(() => setS(d.ai.summary), [d.ai.summary]);
  const run = useAct(() => api.post<{ summary: AiSummary }>(`/comunicacoes/${d.communication.id}/resumo-ia`), [], (r) => setS(r.summary), "Resumo gerado");
  return (
    <Card title={<>Resumo por IA <Badge tone="info">beta</Badge></>} actions={
      <button className="btn small" disabled={run.isPending} onClick={() => run.mutate()}>{run.isPending ? "Lendo…" : s ? "Refazer" : "Resumir"}</button>
    }>
      <ErrorBox error={run.error} />
      {!s ? <div className="muted small">Gera um resumo em linguagem simples, com providências e prazos. Confira sempre com o texto.</div> : (
        <div className="stack gap-6">
          {s.resumo && <div>{s.resumo}</div>}
          <div className="row" style={{ gap: 6 }}>
            {s.ato && <Badge>{s.ato}</Badge>}
            {s.urgencia && <Badge tone={s.urgencia === "alta" ? "bad" : s.urgencia === "media" ? "warn" : "ok"}>urgência {s.urgencia}</Badge>}
            {s.audiencia && <Badge tone="info">audiência {fmtDate(s.audiencia.data)}{s.audiencia.hora ? ` ${s.audiencia.hora}` : ""}</Badge>}
          </div>
          {!!s.providencias?.length && (
            <ul className="ai-list">{s.providencias.map((x, i) => (
              <li key={i}>{x.o_que}{x.prazo_dias ? ` — ${x.prazo_dias} dias${x.tipo_prazo ? ` ${x.tipo_prazo}` : ""}` : ""}
                {x.calc && <div className="small">
                  Prazo sugerido (calculado pelo sistema, {x.calc.regra}, a partir da publicação em {fmtDate(x.calc.publicacao)}): fatal <b>{fmtDate(x.calc.fatal)}</b> · interno {fmtDate(x.calc.interno)}
                  {s.prazoSistema !== undefined && (s.prazoSistema
                    ? s.prazoSistema === x.calc.fatal ? <> <Badge tone="ok">confere com o texto</Badge></> : <> <Badge tone="warn">o texto indica {fmtDate(s.prazoSistema)} — confira</Badge></>
                    : <> <Badge tone="muted">o sistema não achou prazo no texto</Badge></>)}
                </div>}
              </li>
            ))}</ul>
          )}
          {s.observacoes && <div className="muted small">{s.observacoes}</div>}
          <div className="muted small">IA pode errar: confira com o texto. {d.ai.model ? `Modelo ${d.ai.model}` : ""}</div>
        </div>
      )}
    </Card>
  );
}

function Sources({ c }: { c: Panel["communication"] }): JSX.Element {
  return (
    <details className="card ws-details">
      <summary>Fontes da captura ({c.captures.length})</summary>
      <DataTable rows={c.captures} rowKey={(x) => x.id}
        columns={[
          { key: "s", label: "Fonte", width: 100, render: (x) => SOURCE_LABEL[x.source] ?? x.source },
          { key: "e", label: "Identificador", render: (x) => <><span className="mono small">{x.externalId}</span>{x.payload._via && <div className="muted small">{x.payload._via === "oab" ? `achada pela OAB ${x.payload._oab ?? ""}` : `achada pelo nome ${x.payload._via.slice(5)}`}</div>}</> },
          { key: "c", label: "Capturada em", width: 150, render: (x) => fmtDateTime(x.capturedAt) },
        ]} />
      <div className="muted small pad">Para juntar/desagrupar versões, use a lista da Controladoria. ID: <span className="mono">{c.id}</span></div>
    </details>
  );
}

// ------------------------------------------------------- coluna direita

function ProcessCard({ d }: { d: Panel }): JSX.Element {
  const { userName, can } = useLookups();
  const [tab, setTab] = useState<"resumo" | "aberto" | "historico">("resumo");
  const [link, setLink] = useState<EntityRef | null>(null);
  const [newProcess, setNewProcess] = useState(false);
  const inv = [["comunicacao-painel"], ["comunicacoes"], ["controladoria"]];
  const doLink = useAct((processId: string) => api.post(`/comunicacoes/${d.communication.id}/vincular`, { processId }), inv, () => setLink(null), "Processo vinculado");
  const c = d.communication;
  if (!d.process) {
    return (
      <Card title="1. Processo">
        <div className="stack gap-8">
          <div className="warn-box">
            Sem processo vinculado{c.cnjNumber ? " — o número não existe no cadastro" : ""}. Vincule a um processo existente ou cadastre agora
            (número e órgão já vêm preenchidos). Depois aparecem os campos para criar o prazo e a atividade.
          </div>
          {can("comunicacao.triar") && (
            <div className="row nowrap">
              <div style={{ flex: 1 }}><EntityPicker kinds={["processo"]} value={link} onChange={setLink} /></div>
              <button className="btn" disabled={!link || doLink.isPending} onClick={() => doLink.mutate(link!.id)}>Vincular</button>
              <span className="muted small">ou</span>
              <button className="btn primary" onClick={() => setNewProcess(true)}>Cadastrar processo</button>
            </div>
          )}
          <ErrorBox error={doLink.error} />
        </div>
        {newProcess && <NewProcessModal onClose={() => setNewProcess(false)} defaults={{ cnjNumber: c.cnjNumber, organName: d.parsed.organ ?? c.organName }} onCreated={(pid) => doLink.mutate(pid)} />}
      </Card>
    );
  }
  const pr = d.process;
  const pan = d.panorama!;
  const casePersons = pr.case?.persons ?? [];
  const clients = [...pr.persons.filter((x) => x.role === "cliente"), ...casePersons.filter((x) => x.role === "cliente")].filter((x, i, a) => a.findIndex((y) => y.person.id === x.person.id) === i);
  const others = [...pr.persons, ...casePersons.filter((x) => x.role !== "cliente")].filter((x, i, a) => a.findIndex((y) => y.person.id === x.person.id) === i && !clients.some((cl) => cl.person.id === x.person.id));
  return (
    <Card title={<>1. Processo <Link to={`/processos/${pr.id}`} className="small">abrir ↗</Link></>}>
      <Tabs value={tab} onChange={setTab} tabs={[
        { key: "resumo", label: "Resumo" },
        { key: "aberto", label: "Em aberto", count: pan.openActivities.length + pan.openObligations.length },
        { key: "historico", label: "Histórico", count: pan.previous.length },
      ]} />
      {tab === "resumo" && (
        <div className="stack gap-8" style={{ marginTop: 8 }}>
          <div className="kv-grid">
            <div className="span-3"><span>Processo</span><b>{pr.cnjNumber ? <Cnj value={pr.cnjNumber} /> : pr.title}</b></div>
            <div className="span-2"><span>Caso</span><b>{pr.case ? <Link to={`/casos/${pr.case.id}`}>#{pr.case.number} {pr.case.title}</Link> : "sem caso"}</b></div>
            <div><span>Etapa</span><b><StageBadge stageId={pr.stageId} /></b></div>
            <div className="span-2"><span>Órgão</span><b>{pr.organName}</b></div>
            <div><span>Responsável</span><b>{userName(pr.responsibleUserId)}</b></div>
            <div><span>Fase / situação</span><b>{pr.phase} · {pr.situation}</b></div>
            <div><span>Classe</span><b>{pr.className ?? "—"}</b></div>
            <div><span>Última revisão</span><b>{pr.lastReviewedAt ? fmtDate(pr.lastReviewedAt) : "nunca"}</b></div>
          </div>
          <div className="parties">
            <div><div className="fld-label">Cliente(s)</div>{clients.map((x) => <div key={x.person.id}><Link to={`/pessoas/${x.person.id}`}>{x.person.name}</Link></div>)}{!clients.length && <span className="muted">—</span>}</div>
            <div><div className="fld-label">Outras partes</div>{others.map((x) => <div key={x.person.id}>{x.person.name} <span className="muted small">({x.role.replace(/_/g, " ")})</span></div>)}{!others.length && <span className="muted">—</span>}</div>
          </div>
        </div>
      )}
      {tab === "aberto" && (
        <div className="stack gap-8" style={{ marginTop: 8 }}>
          {pan.openObligations.length > 0 && (
            <div><div className="fld-label">Prazos controlados em aberto</div>
              {pan.openObligations.map((o) => <div key={o.id} className="lrow" style={{ cursor: "default" }}><span className="grow">{o.description}</span><Badge tone="bad">fatal {fmtDate(o.fatalAt)}</Badge></div>)}
            </div>
          )}
          <div className="fld-label">Atividades em aberto</div>
          {pan.openActivities.map((a) => (
            <div key={a.id} className="lrow" onClick={() => navigate(`/atividades/${a.id}`)}>
              <div className="grow"><div className="tt">#{a.number} {a.title}</div><div className="mm">{STATE_LABEL[a.state] ?? a.state} · {userName(a.responsibleUserId)}</div></div>
              {a.fatalDueAt && <Badge tone="bad">fatal {fmtDate(a.fatalDueAt)}</Badge>}
              {a.internalDueAt && <Badge tone="info">interno {fmtDate(a.internalDueAt)}</Badge>}
            </div>
          ))}
          {!pan.openActivities.length && <div className="muted small">Nenhuma atividade aberta — cuidado para não duplicar providências.</div>}
        </div>
      )}
      {tab === "historico" && (
        <div className="stack gap-8" style={{ marginTop: 8 }}>
          {pan.previous.map((x) => (
            <div key={x.id} className="lrow" onClick={() => navigate(`/controladoria/intimacoes/${x.id}`)}>
              <div className="grow"><div className="tt">{fmtDate(x.availableAt)} · {x.kind ?? "comunicação"} <Badge tone="muted">{COMM_STATUS_LABEL[x.status] ?? x.status}</Badge></div><div className="mm">{x.text}</div></div>
            </div>
          ))}
          {(pan.movements?.length ?? 0) > 0 && <div className="fld-label">Movimentações no tribunal (DataJud)</div>}
          {pan.movements?.map((m) => (
            <div key={m.id} className="lrow" style={{ cursor: "default" }}>
              <div className="grow"><div className="tt" style={{ fontWeight: m.relevant ? 600 : 400 }}>{fmtDate(m.occurredAt)} · {m.name}</div>{m.complement && <div className="mm">{m.complement}</div>}</div>
              {m.relevant && (m.matchedCommunicationId ? <Badge tone="ok">intimação capturada</Badge> : <Badge tone="warn">sem intimação</Badge>)}
            </div>
          ))}
          {pan.recentDone.length > 0 && <div className="fld-label">Atividades concluídas</div>}
          {pan.recentDone.map((a) => (
            <div key={a.id} className="lrow" onClick={() => navigate(`/atividades/${a.id}`)}>
              <div className="grow"><div className="tt">#{a.number} {a.title}</div><div className="mm">{userName(a.responsibleUserId)} · concluída {fmtDate(a.completedAt)}</div></div>
            </div>
          ))}
          {!pan.previous.length && !pan.recentDone.length && <div className="muted small">Sem histórico.</div>}
        </div>
      )}
    </Card>
  );
}

function Analysis({ d, onDone }: { d: Panel; onDone: () => void }): JSX.Element {
  const { can, catalog } = useLookups();
  const c = d.communication;
  const prazoType = catalog?.activityTypes.find((t) => t.key === "prazo")?.id ?? null;
  const blank = (): Provid => ({
    description: "", days: d.parsed.deadline?.unit === "dias" ? String(d.parsed.deadline.days) : "15",
    fatalAt: d.dates.fatalSugerido ?? "", suggestedFatalAt: d.dates.fatalSugerido ?? "",
    createActivity: true, title: "", typeId: prazoType, responsibleUserId: null, plannedAt: "", internalDueAt: d.dates.internoSugerido ?? "",
  });
  const [decision, setDecision] = useState<"ciencia" | "descartada" | "com_obrigacao">("com_obrigacao");
  const [note, setNote] = useState("");
  const [obs, setObs] = useState<Provid[]>(() => [blank()]);
  const [sel, setSel] = useState(0);
  const [flowAfter, setFlowAfter] = useState(false);
  const [flowFor, setFlowFor] = useState<{ fatal: string | null } | null>(null);
  useEffect(() => { setObs([blank()]); setSel(0); setNote(""); setDecision("com_obrigacao"); setFlowAfter(false); setFlowFor(null); /* nova intimação */ }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const up = (i: number, patch: Partial<Provid>) => setObs((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const triage = useAct(() => api.post(`/comunicacoes/${c.id}/triagem`, {
    decision, note: note || null,
    obligations: decision === "com_obrigacao" ? obs.map((o) => ({
      description: o.description, fatalAt: o.fatalAt || null, suggestedFatalAt: o.suggestedFatalAt || null,
      activity: o.createActivity ? {
        title: o.title || o.description.slice(0, 120), typeId: o.typeId, responsibleUserId: o.responsibleUserId,
        internalDueAt: o.internalDueAt || null, plannedAt: o.plannedAt || null,
      } : undefined,
    })) : [],
  }), [["comunicacao-painel"], ["comunicacoes"], ["controladoria"], ["atividades"], ["painel"]], () => {
    if (flowAfter && decision === "com_obrigacao" && d.process) setFlowFor({ fatal: obs.map((o) => o.fatalAt).filter(Boolean).sort()[0] ?? null });
    else onDone();
  }, "Análise concluída");

  const calc = async (i: number) => {
    const o = obs[i]!;
    if (!d.dates.publicacao || !o.days) return;
    const r = await api.get<{ suggested: string }>(`/controladoria/prazo-sugerido${qs({ from: d.dates.publicacao, days: o.days, organName: c.organName ?? undefined })}`);
    up(i, { suggestedFatalAt: r.suggested, fatalAt: r.suggested, internalDueAt: "" });
  };

  if (!can("comunicacao.triar")) return <Card title="2. Análise"><div className="muted">Você pode ver, mas não analisar esta intimação.</div></Card>;
  const invalid = decision === "com_obrigacao" && (!d.process || obs.some((o) => o.description.trim().length < 3 || (o.createActivity && o.responsibleUserId && !o.internalDueAt)));
  const acts = obs.filter((o) => o.createActivity).length;

  return (
    <Card title="2. O que esta intimação exige?">
      <div className="stack gap-10">
        <div className="seg">
          {(["com_obrigacao", "ciencia", "descartada"] as const).map((k) => (
            <button key={k} type="button" className={decision === k ? "on" : ""} onClick={() => setDecision(k)}>
              {k === "com_obrigacao" ? "Tem prazo / providência" : k === "ciencia" ? "Só ciência" : "Descartar"}
            </button>
          ))}
        </div>
        {decision === "com_obrigacao" && !d.process && <div className="warn-box">Vincule ou cadastre o processo (passo 1) para criar o prazo e a atividade.</div>}
        {decision === "com_obrigacao" && d.process && (
          <>
            {obs.length > 1 && (
              <div className="row" style={{ gap: 4 }}>
                {obs.map((o, i) => <button key={i} className={`chip ${i === sel ? "on" : ""}`} onClick={() => setSel(i)}>Providência {i + 1}{o.description ? `: ${o.description.slice(0, 24)}` : ""}</button>)}
              </div>
            )}
            {obs.map((o, i) => i === sel && (
              <div key={i} className="stack gap-10">
                <FormGrid cols={3}>
                  <Field label="O que precisa ser feito" required span={3}>
                    <input className="input" value={o.description} onChange={(e) => up(i, { description: e.target.value })} placeholder="Ex.: Manifestar sobre a contestação" autoFocus />
                  </Field>
                  <Field label="Prazo (dias úteis)" hint={d.parsed.deadline ? `Detectado no texto: ${d.parsed.deadline.days} ${d.parsed.deadline.unit}` : undefined}>
                    <div className="row nowrap"><input className="input" value={o.days} onChange={(e) => up(i, { days: e.target.value })} /><button className="btn small" onClick={() => void calc(i)}>Calcular</button></div>
                  </Field>
                  <Field label="Prazo fatal" hint={o.suggestedFatalAt ? `Sugerido: ${fmtDate(o.suggestedFatalAt)} (a partir da publicação ${fmtDate(d.dates.publicacao)})` : "Sugestão — a decisão é sua"}>
                    <input type="date" className="input" value={o.fatalAt} onChange={(e) => up(i, { fatalAt: e.target.value, internalDueAt: "" })} />
                  </Field>
                  <Field label="Tipo"><TypeSelect value={o.typeId} onChange={(v) => up(i, { typeId: v })} /></Field>
                  <label className="check span-3"><input type="checkbox" checked={o.createActivity} onChange={(e) => up(i, { createActivity: e.target.checked })} /> Criar atividade para alguém da equipe</label>
                </FormGrid>
                {o.createActivity && (
                  <>
                    <Workload processId={d.process!.id} organName={c.organName} fatal={o.fatalAt || null} offset={d.settings.internoDiasAntes}
                      value={{ userId: o.responsibleUserId, plannedAt: o.plannedAt }}
                      onPick={(v) => up(i, v)} />
                    <FormGrid cols={3}>
                      <Field label="Data planejada" hint="Clique na grade acima"><input type="date" className="input" value={o.plannedAt} onChange={(e) => up(i, { plannedAt: e.target.value })} /></Field>
                      <Field label="Prazo interno" hint={`Padrão: fatal − ${d.settings.internoDiasAntes} dias úteis`}><input type="date" className="input" value={o.internalDueAt} onChange={(e) => up(i, { internalDueAt: e.target.value })} /></Field>
                      <Field label="Título da atividade"><input className="input" value={o.title} onChange={(e) => up(i, { title: e.target.value })} placeholder="(igual à providência)" /></Field>
                    </FormGrid>
                  </>
                )}
                {obs.length > 1 && <div><button className="btn small danger" onClick={() => { setObs(obs.filter((_, j) => j !== i)); setSel(0); }}>Remover esta providência</button></div>}
              </div>
            ))}
            <div><button className="btn small" onClick={() => { setObs([...obs, blank()]); setSel(obs.length); }}>+ outra providência</button></div>
            <label className="check"><input type="checkbox" checked={flowAfter} onChange={(e) => { setFlowAfter(e.target.checked); if (e.target.checked) setObs((xs) => xs.map((x) => ({ ...x, createActivity: false }))); }} />
              {" "}Seguir um fluxo (ex.: Petição: redigir → revisar → protocolar) com este prazo fatal — ao concluir a análise, escolho o fluxo e as pessoas</label>
          </>
        )}
        <Field label="Observação da análise"><textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <ErrorBox error={triage.error} />
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <span className="muted small">{d.nav.nextId ? "Ao concluir, abre a próxima da fila." : "Última da fila."}</span>
          <button className="btn primary" disabled={invalid || triage.isPending} onClick={() => triage.mutate()}>
            {decision === "com_obrigacao" && flowAfter ? "Concluir e iniciar fluxo" : decision === "com_obrigacao" && acts ? `Concluir e criar ${acts > 1 ? `${acts} atividades` : "atividade"}` : "Concluir análise"}
          </button>
        </div>
      </div>
      {flowFor && d.process && <StartFlowModal processId={d.process.id} fatalDueAt={flowFor.fatal} communicationId={c.id} onClose={() => { setFlowFor(null); onDone(); }} />}
    </Card>
  );
}

/** Sugestão de responsável + mapa de carga (pessoas × dias úteis até o fatal). */
function Workload({ processId, organName, fatal, offset, value, onPick }: {
  processId: string; organName: string | null; fatal: string | null; offset: number;
  value: { userId: string | null; plannedAt: string };
  onPick: (v: { responsibleUserId?: string | null; plannedAt?: string; internalDueAt?: string }) => void;
}): JSX.Element {
  const { userName } = useLookups();
  const [filter, setFilter] = useState("");
  const [all, setAll] = useState(false);
  const { data, error } = useQuery({
    queryKey: ["carga", processId, fatal, organName],
    queryFn: () => api.get<Carga>(`/controladoria/carga${qs({ processId, until: fatal ?? undefined, organName: organName ?? undefined })}`),
  });
  // Prazo interno automático quando há fatal e grade carregada.
  const internal = useMemo(() => (data && fatal ? addBusinessBack(fatal, offset, data.days) : ""), [data, fatal, offset]);
  useEffect(() => { if (internal) onPick({ internalDueAt: internal }); }, [internal]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading lines={3} />;
  const suggested = new Set(data.suggestions.map((s) => s.userId));
  const f = filter.trim().toLowerCase();
  let users = [...data.users].sort((a, b) => Number(suggested.has(b.id)) - Number(suggested.has(a.id)) || a.window - b.window);
  if (f) users = users.filter((u) => u.name.toLowerCase().includes(f));
  else if (!all) users = users.filter((u) => suggested.has(u.id) || u.id === value.userId).concat(users.filter((u) => !suggested.has(u.id) && u.id !== value.userId).slice(0, Math.max(0, 6 - suggested.size)));
  const max = Math.max(1, ...data.users.flatMap((u) => Object.values(u.perDay)));
  const heat = (n: number) => (n === 0 ? "h0" : n / max < 0.34 ? "h1" : n / max < 0.67 ? "h2" : "h3");

  return (
    <div className="workload">
      <div className="row" style={{ gap: 6, alignItems: "center" }}>
        <span className="fld-label" style={{ margin: 0 }}>Responsável sugerido:</span>
        {data.suggestions.map((s) => (
          <button key={s.userId} type="button" className={`chip ${value.userId === s.userId ? "on" : ""}`} title={s.reasons.join("; ")}
            onClick={() => onPick({ responsibleUserId: s.userId })}>
            {userName(s.userId)} <span className="small" style={{ opacity: .75 }}>· {s.reasons[0]}</span>
          </button>
        ))}
        <span className="grow" />
        <input className="input" style={{ width: 160 }} placeholder="Buscar pessoa…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        {!f && <button className="btn small ghost" onClick={() => setAll(!all)}>{all ? "Só sugeridos" : "Toda a equipe"}</button>}
      </div>
      <div className="heat-wrap">
        <table className="heat">
          <thead>
            <tr>
              <th />
              {data.days.map((d) => {
                const dt = new Date(`${d.date}T12:00:00Z`);
                return <th key={d.date} className={`${d.holiday ? "hol" : ""} ${d.date === fatal ? "fatal" : ""}`} title={d.holiday ? "Feriado" : d.date === fatal ? "Prazo fatal" : ""}>
                  {"DSTQQSS"[dt.getUTCDay()]}<br />{d.date.slice(8, 10)}/{d.date.slice(5, 7)}
                </th>;
              })}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.id === value.userId ? "me" : ""}>
                <td className="who" onClick={() => onPick({ responsibleUserId: u.id })}>
                  <b>{u.name}</b><div className="muted small">{u.open} abertas{u.overdue ? ` · ${u.overdue} atrasadas` : ""}</div>
                </td>
                {data.days.map((d) => {
                  const n = u.perDay[d.date] ?? 0;
                  const fat = u.fatalPerDay[d.date] ?? 0;
                  const picked = u.id === value.userId && d.date === value.plannedAt;
                  return (
                    <td key={d.date} className={`cell ${d.holiday ? "hol" : heat(n)} ${d.date === fatal ? "fatal" : ""} ${picked ? "picked" : ""}`}
                      title={`${u.name} · ${d.date}: ${n} atividade(s)${fat ? ` · ${fat} prazo(s) fatal(is)` : ""}`}
                      onClick={() => !d.holiday && onPick({ responsibleUserId: u.id, plannedAt: d.date })}>
                      {n || ""}{fat ? <span className="fdot" /> : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="muted small">
        Números = atividades já planejadas no dia · ponto vermelho = prazo fatal da pessoa · coluna com borda = fatal desta intimação.
        Clique num dia para escolher responsável e data planejada. {!fatal && "Informe o prazo fatal para a grade ir até ele."}
      </div>
    </div>
  );
}

function Result({ d }: { d: Panel }): JSX.Element {
  const { userName, can } = useLookups();
  const c = d.communication;
  const verify = useAct((v: { obId: string; result: string }) => api.post(`/obrigacoes/${v.obId}/verificacao`, { result: v.result }), [["comunicacao-painel"], ["controladoria"]]);
  return (
    <Card title="Resultado da análise">
      <div className="muted small">Por {userName(c.analyzedBy)} em {fmtDateTime(c.analyzedAt)}</div>
      {c.analysisNote && <div className="pre">{c.analysisNote}</div>}
      <DataTable rows={c.obligations} rowKey={(o) => o.id} empty="Sem obrigações."
        columns={[
          { key: "d", label: "Obrigação", render: (o) => o.description },
          { key: "f", label: "Fatal", width: 100, render: (o) => fmtDate(o.fatalAt) },
          { key: "a", label: "Atividade", width: 90, render: (o) => (o.activityId ? <Link to={`/atividades/${o.activityId}`}>abrir</Link> : "—") },
          { key: "v", label: "Verificação", width: 220, render: (o) => o.verification === "nao_verificado" && can("comunicacao.verificar") ? (
            <div className="row"><button className="btn small" onClick={() => verify.mutate({ obId: o.id, result: "verificado" })}>Conferido</button><button className="btn small danger" onClick={() => verify.mutate({ obId: o.id, result: "divergente" })}>Divergente</button></div>
          ) : <Badge tone={o.verification === "verificado" ? "ok" : o.verification === "divergente" ? "bad" : "warn"}>{o.verification.replace("_", " ")}</Badge> },
        ]} />
      <ErrorBox error={verify.error} />
    </Card>
  );
}
